import { useCallback } from "react";
import { EVENTS, CallBackProps, Step, StoreHelpers } from "react-joyride";
import { getScrollParent } from "../utils";
import {
  sendCoachJourneyFinishedEvent,
  sendCoachJourneySkippedEvent,
  sendCoachJourneyStepViewedEvent,
} from "../../../utils/googleAnalytics";

export function useJoyrideCallback(
  updatedSteps: Step[],
  helpersRef: React.MutableRefObject<StoreHelpers | null>,
  detachClickRef: React.MutableRefObject<() => void | undefined>,
  onFinish: () => void,
  setRunJourney: (v: boolean) => void,
  setIsRunning: (v: boolean) => void,
  setIsForced: (v: boolean) => void,
  journeyType: string,
) {
  return useCallback(
    (data: CallBackProps) => {
      const { type, step, index, status } = data;
      const total = updatedSteps.length;

      // ---------- GA EVENTS ----------
      if (type === EVENTS.STEP_BEFORE) {
        sendCoachJourneyStepViewedEvent(journeyType, index + 1, total);
      }

      if (status === "finished") {
        sendCoachJourneyFinishedEvent(journeyType);
      }

      if (status === "skipped") {
        sendCoachJourneySkippedEvent(journeyType, index + 1, total);
      }
      // ---------------------------------

      if (type === EVENTS.STEP_AFTER && detachClickRef.current) {
        detachClickRef.current();
        detachClickRef.current = undefined;
      }

      if (type === EVENTS.STEP_BEFORE && step.spotlightClicks) {
        const targetEl =
          typeof step.target === "string"
            ? document.querySelector(step.target)
            : step.target;

        if (targetEl) {
          const goNextSafely = () => {
            const nextStep = updatedSteps[index + 1];
            if (!nextStep) return;

            const exists =
              typeof nextStep.target === "string"
                ? document.querySelector(nextStep.target)
                : nextStep.target;

            if (exists) {
              helpersRef.current?.next();
            } else {
              requestAnimationFrame(goNextSafely);
            }
          };

          const handler = () => goNextSafely();
          targetEl.addEventListener("click", handler, { once: true });
          detachClickRef.current = () =>
            targetEl.removeEventListener("click", handler);
        }
      }

      if (type === EVENTS.STEP_BEFORE && step.isFixed) {
        const targetEl = document.querySelector(
          (step as any).target,
        ) as HTMLElement | null;
        const scrollRoot = getScrollParent(targetEl);

        if ("scrollTo" in scrollRoot) {
          (scrollRoot as HTMLElement | Window).scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } else {
          (scrollRoot as HTMLElement).scrollTop = 0;
        }

        requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
      }

      if (type === EVENTS.STEP_AFTER && step.isFixed) {
        requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
      }

      if (status === "finished" || status === "skipped") {
        setRunJourney(false);
        setIsRunning(false);
        setIsForced(false);
        onFinish();
      }
    },
    [updatedSteps, journeyType],
  );
}
